import type React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Chip,
    Radio,
    RadioGroup,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { TextContext } from 'react-aria-components/Text';
import {
    countParam,
    FIELD_LABEL_KEYS,
    moneyParam,
    rowsParam,
} from '@/pages/catalogImport/notices';
import type { VariantMismatchField } from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerVariantFigure } from './rowDrawerResolutions';

/** Inside the radio's label a `p` is not allowed: the text keeps Typography's look on a `span`. */
const asSpan: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <span {...domProps}>{children}</span>;

/** A radio's value: the number's digits, or `blank` for a blank cell. */
const BLANK_KEY = 'blank';
const keyOf = (value: number | null): string =>
    value === null ? BLANK_KEY : String(value);

/** A price as the app prints money (`₾ 50.00`), the minimum as a count; blank says so. */
const valueText = (
    t: TFunction,
    field: VariantMismatchField,
    value: number | null,
): string => {
    if (value === null) {
        return t('catalogImport.products.drawer.group.blank');
    }
    return field === 'minimumQuantity' ? countParam(value) : moneyParam(value);
};

type RowDrawerVariantFixFigureProps = {
    figure: RowDrawerVariantFigure;
    /** The variant's rows, as the copy names them ("rows 177, 178"). */
    rows: string;
    onPick: (field: VariantMismatchField, value: number | null) => void;
    onApply: (field: VariantMismatchField) => void;
};

/**
 * One differing figure of a variant (sale price, wholesale price or minimum
 * quantity): the sentence naming the variant's rows, a radio card per value
 * with the rows that carry it and the suggested one marked, why it is
 * suggested and its scope, and **Use … for rows …**.
 */
export const RowDrawerVariantFixFigure: React.FC<
    RowDrawerVariantFixFigureProps
> = ({ figure, rows, onPick, onApply }) => {
    const { t } = useTranslation('pages');
    const { field } = figure;
    const fieldName = t(FIELD_LABEL_KEYS[field]);

    return (
        <div
            className="flex flex-col gap-2"
            {...getNodeTestIdProp(
                `catalog-import-row-drawer-variant-fix-${field}`,
            )}
        >
            <Typography type="body-sm">
                {t('catalogImport.products.drawer.fix.variantFields.intro', {
                    field: fieldName,
                    rows,
                })}
            </Typography>
            <RadioGroup
                aria-label={t(
                    'catalogImport.products.drawer.fix.variantFields.legend',
                    { field: fieldName, rows },
                )}
                className="flex flex-col gap-2"
                onChange={(key) => {
                    const entry = figure.values.find(
                        (candidate) => keyOf(candidate.value) === key,
                    );
                    if (entry) {
                        onPick(field, entry.value);
                    }
                }}
                value={keyOf(figure.picked)}
            >
                {figure.values.map((entry) => {
                    const key = keyOf(entry.value);
                    return (
                        <Card
                            className="p-0"
                            key={key}
                            variant="secondary"
                            {...getNodeTestIdProp(
                                `catalog-import-row-drawer-variant-value-${field}-${key}`,
                            )}
                        >
                            <Radio className="mt-0 w-full" value={key}>
                                <Radio.Content className="w-full items-start gap-3 p-3">
                                    <Radio.Control className="mt-0.5">
                                        <Radio.Indicator />
                                    </Radio.Control>
                                    {/* The radio offers its text only a
                                        description slot; these lines are
                                        part of its label. */}
                                    <TextContext.Provider value={null}>
                                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                                            <span className="flex flex-wrap items-center gap-2">
                                                <Typography
                                                    className="tabular-nums"
                                                    render={asSpan}
                                                    type="body-sm"
                                                    weight="medium"
                                                >
                                                    {valueText(
                                                        t,
                                                        field,
                                                        entry.value,
                                                    )}
                                                </Typography>
                                                {entry.isSuggested ? (
                                                    <Chip
                                                        color="accent"
                                                        size="sm"
                                                        variant="soft"
                                                    >
                                                        {t(
                                                            'catalogImport.products.drawer.fix.suggested',
                                                        )}
                                                    </Chip>
                                                ) : null}
                                            </span>
                                            <Typography
                                                color="muted"
                                                render={asSpan}
                                                type="body-sm"
                                            >
                                                {t(
                                                    'catalogImport.products.drawer.fix.rows',
                                                    {
                                                        count: entry.rows
                                                            .length,
                                                        rows: rowsParam(
                                                            entry.rows,
                                                        ),
                                                    },
                                                )}
                                            </Typography>
                                        </span>
                                    </TextContext.Provider>
                                </Radio.Content>
                            </Radio>
                        </Card>
                    );
                })}
            </RadioGroup>
            <Typography color="muted" type="body-xs">
                {t(
                    'catalogImport.products.drawer.fix.variantFields.suggestedWhy',
                )}{' '}
                {t('catalogImport.products.drawer.fix.variantFields.scope', {
                    rows,
                })}
            </Typography>
            <div>
                <Button
                    onPress={() => onApply(field)}
                    size="sm"
                    variant="secondary"
                    {...getNodeTestIdProp(
                        `catalog-import-row-drawer-variant-apply-${field}`,
                    )}
                >
                    {t(
                        'catalogImport.products.drawer.fix.variantFields.apply',
                        {
                            value: valueText(t, field, figure.picked),
                            rows,
                        },
                    )}
                </Button>
            </div>
        </div>
    );
};
